<script setup>
import { computed, onMounted, ref } from "vue"
import { useRoute } from "vue-router"
import Layout from "../../layouts/main.vue"
import PageHeader from "@/components/page-header"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"

const walletTransactions = computed(() => store.state.walletTransactions)

import useUsers from "../../use/useUsers"

import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const route = useRoute()
const userId = route.params.userId

const title = "تراکنش های کیف پول کاربر"
const items = ref([
  {
    text: "داشبورد",
    href: "/",
  },
  {
    text: "تراکنش های کیف پول کاربر",
    active: true,
  },
])

const {
  transactionType,
  actionType,
  createdAtDate,
  perPage,
  sortDirection,
  getWalletTransactions,
} = useUsers()

const sortField = ref("created_at")

const formattedPrices = computed(() => {
  return walletTransactions.value.data.map((walletTransaction) =>
    new Intl.NumberFormat("fa-IR").format(walletTransaction.amount)
  )
})

onMounted(() => {
  getWalletTransactions(userId, null)
})

const transactionTypeLabels = {
  ONLINE_PAYMENT: "پرداخت آنلاین",
  THROUGH_ADMIN_PANEL: "از طریق پنل مدیریت",
  ORDER_PAYMENT: "پرداخت سفارش",
  CANCEL_ORDER: "لفو سفارش",
}

const actionTypeLabels = {
  DECREASE: "برداشت شده",
  INCREASE: "پرداخت شده",
}

const actionTypeSign = {
  DECREASE: "-",
  INCREASE: "+",
}

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getWalletTransactions(userId, link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

function sortWalletTransactions(field) {
  if (field === sortField.value) {
    if (sortDirection.value === "desc") {
      sortDirection.value = "asc"
    } else {
      sortDirection.value = "desc"
    }
  } else {
    sortField.value = field
    sortDirection.value = "asc"
  }

  getWalletTransactions(userId, null)
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <b-row>
      <b-col lg="12">
        <b-card no-body id="userList">
          <b-card-header class="border-bottom-dashed">
            <b-row class="g-4 align-items-center">
              <b-col sm>
                <div>
                  <h5 class="card-title mb-0">تراکنش های کیف پول کاربر</h5>
                </div>
              </b-col>
              <b-col sm="auto">
                <div class="d-flex flex-wrap align-items-start gap-2"></div>
              </b-col>
            </b-row>
          </b-card-header>

          <!-- user wallet transactions table  -->

          <b-card-body class="border-bottom-dashed border-bottom">
            <b-row class="g-3">
              <b-col sm="3">
                <select
                  @change="getWalletTransactions(userId, null)"
                  v-model="transactionType"
                  class="form-select mb-3"
                  aria-label="Default select example"
                >
                  <option value="" selected>انتخاب نوع تراکنش</option>
                  <option
                    v-for="(label, value) in transactionTypeLabels"
                    :key="value"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select>
              </b-col>

              <b-col sm="3">
                <select
                  @change="getWalletTransactions(userId, null)"
                  v-model="actionType"
                  class="form-select mb-3"
                  aria-label="Default select example"
                >
                  <option value="" selected>انتخاب نوع عملیات</option>
                  <option
                    v-for="(label, value) in actionTypeLabels"
                    :key="value"
                    :value="value"
                  >
                    {{ label }}
                  </option>
                </select>
              </b-col>

              <b-col sm="3">
                <custom-date-picker
                  v-model="createdAtDate"
                  @change="getWalletTransactions(userId, null)"
                ></custom-date-picker>
              </b-col>

              <b-col sm="3">
                <select
                  @change="getWalletTransactions(userId, null)"
                  v-model="perPage"
                  class="form-select mb-3"
                  aria-label="Default select example"
                >
                  <option value="" selected>نمایش تعداد ردیف</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                </select>
              </b-col>
            </b-row>
          </b-card-body>
          <b-card-body>
            <div>
              <div class="table-responsive table-card mb-1">
                <table class="table align-middle" id="userTable">
                  <thead class="table-light text-muted">
                    <tr>
                      <TableHeaderCell
                        field="transaction_type"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortWalletTransactions('transaction_type')"
                      >
                        نوع تراکنش
                      </TableHeaderCell>
                      <TableHeaderCell
                        field="amount"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortWalletTransactions('amount')"
                      >
                        مبلغ
                      </TableHeaderCell>
                      <TableHeaderCell
                        field="created_at"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortWalletTransactions('created_at')"
                      >
                        تاریخ و زمان
                      </TableHeaderCell>
                      <TableHeaderCell
                        field="updated_by"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @click="sortWalletTransactions('updated_by')"
                      >
                        توسط
                      </TableHeaderCell>
                    </tr>
                  </thead>
                  <tbody
                    class="list"
                    v-if="
                      walletTransactions.loading ||
                      !walletTransactions.data.length
                    "
                  >
                    <tr>
                      <td colspan="8">
                        <div class="text-center">
                          <b-spinner
                            v-if="walletTransactions.loading"
                            variant="dark"
                            style="width: 3rem; height: 3rem"
                          ></b-spinner>
                          <div v-else>
                            <lottie
                              class="avatar-xl"
                              colors="primary:#121331,secondary:#08a88a"
                              :options="defaultOptions"
                              :height="75"
                              :width="75"
                            />
                            <h5 class="mt-2">هیچ تراکنشی وجود ندارد!</h5>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody class="list" v-else>
                    <tr
                      v-for="(
                        walletTransaction, index
                      ) of walletTransactions.data"
                      :key="index"
                    >
                      <td class="transaction_type">
                        {{
                          transactionTypeLabels[
                            walletTransaction.transactionType
                          ]
                        }}
                      </td>
                      <td class="amount">
                        <span
                          :class="{
                            'text-danger':
                              walletTransaction.actionType == 'DECREASE',
                            'text-success':
                              walletTransaction.actionType == 'INCREASE',
                          }"
                        >
                          {{ formattedPrices[index]
                          }}{{
                            actionTypeSign[walletTransaction.actionType]
                          }}</span
                        >
                      </td>
                      <td class="created_at">
                        {{ toPersianDateTime(walletTransaction.dateTime) }}
                      </td>
                      <td class="updatedby">
                        {{ walletTransaction.updatedBy }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                class="d-flex justify-content-end"
                v-if="walletTransactions.total > walletTransactions.limit"
              >
                <div class="pagination-wrap hstack gap-2" style="display: flex">
                  <ul class="pagination listjs-pagination mb-0">
                    <li
                      v-for="(link, i) of walletTransactions.links"
                      :key="i"
                      class="page-item"
                      :class="{
                        disabled: !link.url,
                        active: link.active || !link.url,
                      }"
                    >
                      <span
                        class="page-link"
                        v-if="link.active"
                        v-html="link.label"
                      ></span>

                      <a
                        v-else
                        href="#"
                        @click="getForPage($event, link)"
                        class="page-link"
                        v-html="link.label"
                      >
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>

<style>
.form-select {
  display: block;
  width: 100%;
  padding: 0.5rem 0.9rem 0.5rem 2.7rem !important;
  background-position: left 0.9rem center !important;
}

.invalid-feedback {
  display: block !important;
}

.vpd-icon-btn {
  margin-bottom: 0 !important;
  padding: 0.5rem 0.9rem !important;
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}
.vpd-input-group input {
  line-height: 1.5 !important;
  padding: 0.5rem 0.9rem !important;
}
</style>
